import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import bootConfig from 'utils/bootConfig';
import { Tool, Link } from 'types';
import { LinkImportLogsResponse } from 'types/ImportLog';
import { BulkInsertLinksResult } from 'types/BulkInsertLinks';
import {
  BulkExportLinkItem,
  ALFRESCO_EXPORT_DEFAULT_FILENAME,
} from 'types/BulkExportLinks';
import type { Location } from 'types/Location';
import type ConnexusLocation from 'types/ConnexusLocation';

const sanitizeFilename = (
  input?: string,
  fallback = ALFRESCO_EXPORT_DEFAULT_FILENAME
): string => {
  if (!input) return fallback;

  let name = input.trim().replace(/^"(.+)"$/, '$1');
  name = name.replace(/^.*[\\/]/, '');
  name = name
    .split('')
    .filter((ch) => {
      const code = ch.charCodeAt(0);
      return !((code >= 0 && code <= 31) || (code >= 128 && code <= 159));
    })
    .join('');
  name = name.replace(/[\\/:*?"<>|]+/g, '_');

  name = name.replace(/\.{2,}/g, '.').trim();
  if (!name) return fallback;
  const MAX = 200;
  if (name.length > MAX) {
    const extIndex = name.lastIndexOf('.');
    if (extIndex > 0 && name.length - extIndex <= 10) {
      const ext = name.slice(extIndex);
      const base = name.slice(0, MAX - ext.length);
      name = `${base}${ext}`;
    } else {
      name = name.slice(0, MAX);
    }
  }

  return name;
};

export const apiSlice = createApi({
  reducerPath: 'apiSlice',
  baseQuery: fetchBaseQuery({
    baseUrl: bootConfig.ltiServiceApiProxyBaseUri,
  }),
  tagTypes: [
    'Tool',
    'Link',
    'Log',
    'Location',
    'RootLocations',
    'ChildLocations',
  ],
  endpoints: (builder) => ({
    // Tools Endpoints
    getToolsList: builder.query({
      query: () => '/tools',
      providesTags: ['Tool'],
    }),
    getTool: builder.query<Tool, string>({
      query: (id: string) => `/tool/${id}`,
      providesTags: ['Tool'],
    }),
    createTool: builder.mutation<Tool, Partial<Tool>>({
      query: (tool: Tool) => ({
        url: '/tool/create',
        method: 'POST',
        body: tool,
      }),
      invalidatesTags: ['Tool'],
    }),
    updateTool: builder.mutation<Tool, Partial<Tool>>({
      query: (tool: Tool) => {
        return {
          url: `/tool/${tool.id}`,
          method: 'PUT',
          body: tool,
        };
      },
      invalidatesTags: ['Tool'],
    }),

    // Links Endpoints
    getLinksList: builder.query({
      query: (queryString: string) => `/links${queryString}`,
      providesTags: ['Link'],
    }),
    getLink: builder.query<Link, string>({
      query: (id: string) => `/link/${id}`,
      providesTags: ['Link'],
    }),
    createLink: builder.mutation<Link, Partial<Link>>({
      query: (link: Link) => ({
        url: '/link/create',
        method: 'POST',
        body: link,
      }),
      invalidatesTags: ['Link'],
    }),
    updateLink: builder.mutation<Link, Partial<Link>>({
      query: (link: Link) => ({
        url: `/link/${link.id}`,
        method: 'PUT',
        body: link,
      }),
      invalidatesTags: ['Link'],
    }),
    bulkInsertLinks: builder.mutation<
      BulkInsertLinksResult,
      { toolId: string; fileName: string; sessionId: number }
    >({
      query: ({ toolId, fileName, sessionId }) => ({
        url: `/link/${toolId}/bulk-insert-links?fileName=${fileName}&sessionId=${sessionId}`,
        method: 'POST',
        body: {
          fileName,
          sessionId,
        },
      }),
    }),

    bulkImportLinksValidation: builder.mutation<
      {
        isValid: boolean;
        statusCode: number;
        errorMessage?: string;
        successFileName?: string;
        sessionId?: number;
      },
      { toolId: string; file: File }
    >({
      query: ({ toolId, file }) => {
        const formData = new FormData();
        formData.append('uploadedFile', file);

        return {
          url: `/link/${toolId}/validate-bulk-import-links-file`,
          method: 'POST',
          body: formData,
          responseHandler: async (response) => {
            const contentType = response.headers.get('content-type');
            if (contentType?.includes('application/json')) {
              const json = await response.json();
              return {
                isValid: json.isValid as boolean,
                statusCode: json.statusCode as number,
                errorMessage: json.errorMessage as string,
                successFileName: json.successFileName as string,
                sessionId: json.sessionId as number,
              };
            }
            throw new Error('Unexpected response format from validation API');
          },
        };
      },
      invalidatesTags: ['Link'],
    }),

    downloadToolTemplate: builder.mutation<void, string>({
      query: (toolId: string) => ({
        url: `/tool/${toolId}/download-template`,
        method: 'GET',
        responseHandler: async (response) => {
          const blob = await response.blob();
          const now = new Date();
          const dateTime = now.toISOString().replace(/[:.]/g, '-');
          const filename = `${toolId}_${dateTime}.csv`;
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        },
      }),
    }),

    getImportLogs: builder.query<
      LinkImportLogsResponse,
      { pageIndex: number; pageSize: number }
    >({
      query: (searchCriteria) => ({
        url: `/log/linkimport-logs`,
        method: 'POST',
        body: searchCriteria,
      }),
    }),

    downloadImportErrorFile: builder.mutation<void, string>({
      query: (sessionId: string) => ({
        url: `/log/linkimport-logs/${sessionId}/error-file`,
        method: 'GET',
        responseHandler: async (response) => {
          if (!response.ok) {
            throw new Error('Failed to download error file');
          }
          const blob = await response.blob();
          const now = new Date();
          const dateTime = now.toISOString().replace(/[:.]/g, '-');
          const filename = `ErrorLog_${sessionId}_${dateTime}.csv`;
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        },
      }),
    }),

    bulkExportLinks: builder.mutation<void, { links: BulkExportLinkItem[] }>({
      query: (payload) => ({
        url: '/links/bulk-export',
        method: 'POST',
        body: payload,
        responseHandler: async (response) => {
          if (!response.ok) {
            throw new Error('Failed to generate export file');
          }
          const blob = await response.blob();
          const contentDisposition =
            response.headers.get('Content-Disposition') || '';
          let rawFilename: string | undefined;
          const filenameStarMatch = contentDisposition.match(
            /filename\*\s*=\s*(?:[^']*'')?([^;]+)/i
          );
          if (filenameStarMatch) {
            const encoded = filenameStarMatch[1]
              .trim()
              .replace(/^"(.+)"$/, '$1');
            try {
              rawFilename = decodeURIComponent(encoded);
            } catch {
              rawFilename = encoded;
            }
          } else {
            const filenameMatch = contentDisposition.match(
              /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
            );
            rawFilename = filenameMatch?.[1];
          }

          const filename = sanitizeFilename(rawFilename);
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        },
      }),
    }),

    // Locations Endpoints - Updated to return string array directly
    getLocations: builder.query<string[], void>({
      query: () => '/locations',
      providesTags: ['Location'],
    }),

    getRootLocations: builder.query<Location[], void>({
      query: () => ({
        url: '/locations/root',
        method: 'GET',
        responseHandler: async (response) => {
          if (!response.ok) {
            throw new Error('Failed to fetch root locations');
          }

          const rootLocations: Location[] = [];
          const data = await response.json();

          data.forEach((item: ConnexusLocation) => {
            let location: Location;
            if (item.hasChildren) {
              location = {
                id: item.id,
                name: item.name,
                locationLevel: item.locationLevel,
                path: item.path,
                children: [],
              };
            } else {
              location = {
                id: item.id,
                name: item.name,
                locationLevel: item.locationLevel,
                path: item.path,
              };
            }
            rootLocations.push(location);
          });

          return rootLocations;
        },
      }),
      providesTags: ['RootLocations'],
    }),

    getRootChildLocations: builder.query<Location[], string>({
      query: (parentId: string) => ({
        url: `/locations/${parentId}/children`,
        method: 'GET',
        responseHandler: async (response) => {
          if (!response.ok) {
            throw new Error(
              `Failed to fetch child locations for parent ID: ${parentId}`
            );
          }

          const childLocations: Location[] = [];
          const data = await response.json();

          data.forEach((item: ConnexusLocation) => {
            let location: Location;
            if (item.hasChildren) {
              location = {
                id: item.id,
                name: item.name,
                locationLevel: item.locationLevel,
                path: item.path,
                children: [],
              };
            } else {
              location = {
                id: item.id,
                name: item.name,
                locationLevel: item.locationLevel,
                path: item.path,
              };
            }
            childLocations.push(location);
          });

          return childLocations;
        },
      }),
      providesTags: ['ChildLocations'],
    }),
  }),
});

export const {
  useGetToolsListQuery,
  useGetToolQuery,
  useCreateToolMutation,
  useUpdateToolMutation,
  useGetLinksListQuery,
  useGetLinkQuery,
  useCreateLinkMutation,
  useUpdateLinkMutation,
  useBulkImportLinksValidationMutation,
  useDownloadToolTemplateMutation,
  useDownloadImportErrorFileMutation,
  useBulkInsertLinksMutation,
  useBulkExportLinksMutation,
  useGetImportLogsQuery,
  useGetLocationsQuery,
  useGetRootLocationsQuery,
  useLazyGetRootChildLocationsQuery,
} = apiSlice;
